import React from "react";
import { useParams } from "react-router-dom";
import { Spin, Result } from "antd";
import ReportDocument from "components/Order/Detail/components/ReportDocument";
import {
  buildMaintenanceViewModels,
  resolvePersistedPhotoSelection,
} from "components/Order/Detail/components/reportModel";
import { resolveSelectedPhotos } from "components/Order/Detail/components/reportPhotos";
import PrintGate from "./PrintGate";
import { useOrderReportData } from "./useOrderReportData";

// ---------------------------------------------------------------------------
// /print/informe/:id — visited by jag-backend's Puppeteer PDF route
// (GET /order/:id/informe/pdf) and, for manual QA, by a developer's own
// browser. No app chrome — just the printable report, reusing
// ReportDocument (the SAME presentational component the interactive
// Informe tab already delegates to) with `interactive={false}` — a static
// snapshot, no photo toggling/compression/zip.
// ---------------------------------------------------------------------------

const InformePrintContent = ({ id }) => {
  const { loading, error, order, maintenances, maxPhotos } = useOrderReportData(id);

  if (error) {
    return <Result status="error" title="No se pudo cargar el informe" />;
  }

  if (loading) {
    return (
      <div style={{ width: "100%", padding: 80, textAlign: "center" }}>
        <Spin spinning tip="Cargando..." />
      </div>
    );
  }

  const viewModels = buildMaintenanceViewModels(maintenances);

  // This static, session-less page is exactly the gap the interactive
  // Informe tab's manual photo curation used to fall into: Report.js's
  // `photoSelection` only ever lived in that browser's memory, so a fresh
  // Puppeteer-driven load here always fell back to the config default. Now
  // it reads the persisted stable-ID-keyed selection (order.
  // informePhotoSelection, PUT'd by Report.js's autosave) and expands it
  // back into the positional shape resolveSelectedPhotos expects — an
  // activity the user never manually touched simply isn't in the map, so
  // resolveSelectedPhotos falls back to its own default behavior for it.
  const persistedSelection = resolvePersistedPhotoSelection(
    viewModels,
    order.informePhotoSelection
  );

  return (
    <ReportDocument
      order={{
        type: order.type,
        code: order.code,
        customer: order.customer,
        subsidiary: order.subsidiary,
      }}
      maintenances={viewModels}
      resumenEjecutivoAI={order.resumenEjecutivoAI}
      deliver={order.deliver}
      created_by={order.created_by}
      maxPhotos={maxPhotos}
      interactive={false}
      showStats={true}
      resolveSelectedPhotos={(mIdx, tIdx, aIdx, files) =>
        resolveSelectedPhotos(mIdx, tIdx, aIdx, files, persistedSelection, maxPhotos)
      }
      compressedImages={{}}
    />
  );
};

const InformePrintPage = () => {
  const { id } = useParams();

  return (
    <PrintGate>
      <InformePrintContent id={id} />
    </PrintGate>
  );
};

export default InformePrintPage;
